/**
 * Self-describing control availability — which Lab controls are active
 * given the current sim/render/audio bag, and what to change to unlock them.
 *
 * Pure functions only. The control panel paints disabled state + unlock hints;
 * physics still honors stored values if something is "latent" (e.g. gravity
 * strength while mode is none) so presets/URL round-trips stay honest.
 */

import type { AudioParams } from "../audio/params";
import type { RenderParams, SimParams } from "../sim/params";

export type ControlAvailabilityContext = {
  sim: SimParams;
  render: RenderParams;
  audio: AudioParams;
};

export type ControlAvailability = {
  /** Control id from controls-schema.json */
  id: string;
  /** When false, the widget is disabled and an unlock hint is shown. */
  active: boolean;
  /**
   * Short, actionable copy when inactive.
   * Prefer "To use this: …" so the panel can show it as-is.
   */
  reason?: string;
  /** Glossary / control ids the user should change (for tests + future deep-links). */
  unlockVia?: string[];
  /**
   * Soft guidance while still active (e.g. subtle default γ).
   * Never disables the control — only annotates.
   */
  note?: string;
};

type Rule = (ctx: ControlAvailabilityContext) => ControlAvailability | null;

function inactive(
  id: string,
  reason: string,
  unlockVia: string[],
): ControlAvailability {
  return { id, active: false, reason, unlockVia };
}

function note(id: string, text: string): ControlAvailability {
  return { id, active: true, note: text };
}

function isRingTopology(topology: string): boolean {
  return (
    topology === "ring" ||
    topology === "multi_ring" ||
    topology.includes("ring")
  );
}

function isGridInit(init: string): boolean {
  return init === "grid" || init.startsWith("grid");
}

function inertialEffective(sim: SimParams): boolean {
  return sim.motionMode === "inertial" && sim.model !== "reynolds";
}

function gravityOn(sim: SimParams): boolean {
  return sim.gravityMode !== "none";
}

/**
 * Ordered rules. First matching rule per control id wins (see resolve).
 * Keep reasons short enough for a one-line field hint.
 */
const RULES: readonly Rule[] = [
  // —— Motion integrator family ——
  (ctx) => {
    if (ctx.sim.model !== "reynolds") return null;
    return {
      id: "motion_mode",
      active: true,
      note: "Reynolds ignores inertial mode — the flock model owns velocity. Switch Model to Friends & enemies to use Integrator = Inertial.",
    };
  },

  (ctx) => {
    if (inertialEffective(ctx.sim)) return null;
    if (ctx.sim.model === "reynolds") {
      return inactive(
        "velocity_damping",
        "To use inertia memory: set Model to Friends & enemies (not Reynolds).",
        ["model"],
      );
    }
    return inactive(
      "velocity_damping",
      "To use inertia memory: set Integrator to Inertial (momentum).",
      ["motion_mode"],
    );
  },

  (ctx) => {
    if (inertialEffective(ctx.sim)) return null;
    if (ctx.sim.model === "reynolds") {
      return inactive(
        "social_gain",
        "To use social gain: set Model to Friends & enemies (not Reynolds).",
        ["model"],
      );
    }
    return inactive(
      "social_gain",
      "To use social gain: set Integrator to Inertial (momentum).",
      ["motion_mode"],
    );
  },

  // —— Gravity family ——
  // Gravity works under kinematic (x' = x + u + g). Do NOT lock gravity_mode
  // behind inertial — that was a common misconception; default γ is just subtle.
  (ctx) => {
    if (!gravityOn(ctx.sim)) return null;
    if (ctx.sim.gravityMode === "type_com" && ctx.sim.typeCount <= 1) {
      return {
        id: "gravity_mode",
        active: true,
        note: "Family COM needs Families (T) ≥ 2 — otherwise it behaves like a single COM well.",
      };
    }
    if (ctx.sim.gravityMode === "wells" && (!ctx.sim.wells || ctx.sim.wells.length === 0)) {
      return {
        id: "gravity_mode",
        active: true,
        note: "Fixed wells needs preset well positions (try the Wells teach preset). Empty wells → no pull.",
      };
    }
    if (ctx.sim.gravityStrength > 0 && ctx.sim.gravityStrength <= 0.0015) {
      return {
        id: "gravity_mode",
        active: true,
        note: "Gravity is on. Default strength is a gentle accent — raise Gravity strength if the bowl is hard to see.",
      };
    }
    return null;
  },

  (ctx) => {
    if (gravityOn(ctx.sim)) {
      if (ctx.sim.gravityStrength <= 0.0015) {
        return note(
          "gravity_strength",
          "Default is subtle (~5% of friend step). Raise toward 0.003–0.008 to see a clear bowl.",
        );
      }
      return null;
    }
    return inactive(
      "gravity_strength",
      "To use gravity strength: set Gravity to Origin, Center of mass, or another mode (not None).",
      ["gravity_mode"],
    );
  },

  (ctx) => {
    if (gravityOn(ctx.sim)) return null;
    return inactive(
      "gravity_softening",
      "To use gravity softening: turn Gravity on (any mode except None).",
      ["gravity_mode"],
    );
  },

  (ctx) => {
    if (gravityOn(ctx.sim)) return null;
    return inactive(
      "particle_mass",
      "To use mass (g = F/m): turn Gravity on. Mass only scales the gravity layer, not Woods social steps.",
      ["gravity_mode"],
    );
  },

  // —— Topology / init ——
  (ctx) => {
    if (ctx.sim.freezeTopology) {
      return inactive(
        "rechoose_prob",
        "To change rechoose: uncheck Freeze topology (freeze forces rechoose to 0).",
        ["freeze_topology"],
      );
    }
    return null;
  },

  (ctx) => {
    if (isRingTopology(ctx.sim.topology)) return null;
    return inactive(
      "ring_stride",
      "To use ring stride: set Topology to a ring mode (ring / multi-ring).",
      ["topology"],
    );
  },

  (ctx) => {
    if (isGridInit(ctx.sim.initPositions)) return null;
    return inactive(
      "grid_extent",
      "To use grid extent: set Init positions to Grid.",
      ["init_positions"],
    );
  },

  (ctx) => {
    if (isGridInit(ctx.sim.initPositions)) return null;
    return inactive(
      "grid_jitter",
      "To use grid jitter: set Init positions to Grid.",
      ["init_positions"],
    );
  },

  (ctx) => {
    if (ctx.sim.localTopology && !ctx.sim.spatialHash) {
      return {
        id: "local_topology",
        active: true,
        note: "Local rechoose works best with Spatial hash on (neighborhood queries).",
      };
    }
    return null;
  },

  (ctx) => {
    if (ctx.sim.relationProfile !== "classic_fe" && ctx.sim.typeCount <= 1) {
      return {
        id: "relation_profile",
        active: true,
        note: "Multi-family profiles need Families (T) ≥ 2.",
      };
    }
    return null;
  },

];

const CAMERA_3D_IDS = [
  "show_grid",
  "show_gnomon",
  "camera_distance",
  "camera_yaw",
  "camera_pitch",
  "camera_fov_preset",
  "camera_fov",
  "auto_rotate",
] as const;

const AUDIO_WHEN_ENABLED_IDS = [
  "audio_master_gain",
  "audio_kinematics",
  "audio_gravity",
  "audio_topology",
  "audio_pitch_amount",
] as const;

/**
 * Resolve availability for one control id. Unknown ids → active.
 */
export function controlAvailability(
  id: string,
  ctx: ControlAvailabilityContext,
): ControlAvailability {
  // Camera batch
  if ((CAMERA_3D_IDS as readonly string[]).includes(id) && ctx.sim.dim !== 3) {
    return inactive(
      id,
      "To use camera controls: set Dimension to 3D (orbit stage).",
      ["dim"],
    );
  }

  // Audio batch
  if (
    (AUDIO_WHEN_ENABLED_IDS as readonly string[]).includes(id) &&
    !ctx.audio.enabled
  ) {
    return inactive(id, "To use this: enable sound (Audio → Enable sound).", [
      "audio_enabled",
    ]);
  }

  for (const rule of RULES) {
    const hit = rule(ctx);
    if (hit && hit.id === id) return hit;
  }

  return { id, active: true };
}

/**
 * Map of every known gated control → availability (for paint + tests).
 * Includes active-with-note entries.
 */
export function allControlAvailability(
  ctx: ControlAvailabilityContext,
): Map<string, ControlAvailability> {
  const ids = new Set<string>([
    ...CAMERA_3D_IDS,
    ...AUDIO_WHEN_ENABLED_IDS,
    "motion_mode",
    "velocity_damping",
    "social_gain",
    "gravity_mode",
    "gravity_strength",
    "gravity_softening",
    "particle_mass",
    "rechoose_prob",
    "ring_stride",
    "grid_extent",
    "grid_jitter",
    "local_topology",
    "relation_profile",
  ]);

  const out = new Map<string, ControlAvailability>();
  for (const id of ids) {
    out.set(id, controlAvailability(id, ctx));
  }
  return out;
}

/** Test helper: list currently inactive control ids. */
export function inactiveControlIds(ctx: ControlAvailabilityContext): string[] {
  return [...allControlAvailability(ctx).values()]
    .filter((a) => !a.active)
    .map((a) => a.id)
    .sort();
}
